/**
 * Capabilities Hub — top-level category hub for /capabilities.
 * Cross-references all .md files under content/capabilities/
 *
 * Lists all 20 capabilities (mixed slugs: some flat, some nested under /capabilities/).
 */

export const capabilitiesHubMeta = {
  title: "AI Perception Capabilities | 20 Capability Matrix | Perception X2",
  description:
    "20 proprietary capabilities that compose the Perception X2 perception amplification stack: narrative engineering, content generation, multi-platform deployment, organic amplification, influencer orchestration, crisis response, and more.",
  canonical: "https://perception.ac/capabilities",
  slug: "capabilities",
};

export interface CapabilityChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  category: "creation" | "deployment" | "intelligence" | "response" | "optimization";
}

export const capabilitiesList: CapabilityChild[] = [
  {
    code: "C01",
    title: "Narrative Engineering",
    description: "AI-orchestrated narrative architecture. Craft compelling, authentic narratives that resonate across audiences and platforms.",
    href: "/capabilities/narrative-engineering",
    icon: "PenTool",
    accent: "crimson",
    category: "creation",
  },
  {
    code: "C02",
    title: "Content Generation",
    description: "AI-powered content creation across text, image, and video — 50+ templates, 100+ languages, 95–98% organic authenticity.",
    href: "/capabilities/content-generation",
    icon: "FileText",
    accent: "purple",
    category: "creation",
  },
  {
    code: "C03",
    title: "Multi-Platform Deployment",
    description: "Simultaneous, coordinated deployment across 50+ platforms — adapting to each algorithm, audience, and format.",
    href: "/capabilities/multi-platform-deployment",
    icon: "LayoutGrid",
    accent: "cyan",
    category: "deployment",
  },
  {
    code: "C04",
    title: "Organic Amplification",
    description: "Engineered organic visibility that bypasses paid channels — sustainable reach with maximum authenticity.",
    href: "/organic-amplification",
    icon: "TrendingUp",
    accent: "emerald",
    category: "deployment",
  },
  {
    code: "C05",
    title: "Influencer Orchestration",
    description: "Strategic coordination of influencer networks across tiers — from nano-influencers to major thought leaders.",
    href: "/influencer-orchestration",
    icon: "Network",
    accent: "amber",
    category: "deployment",
  },
  {
    code: "C06",
    title: "Crisis Narrative Response",
    description: "Rapid-response counter-narrative systems that activate within minutes of threat detection.",
    href: "/crisis-narrative-response",
    icon: "Siren",
    accent: "rose",
    category: "response",
  },
  {
    code: "C07",
    title: "Emotional Resonance",
    description: "144-dimensional emotional resonance mapping — far beyond conventional 5–10 dimension sentiment analysis.",
    href: "/capabilities/emotional-resonance",
    icon: "Heart",
    accent: "violet",
    category: "intelligence",
  },
  {
    code: "C08",
    title: "Audience Targeting",
    description: "Precision audience segmentation across demographics, psychographics, behaviors, and platform-specific signals.",
    href: "/capabilities/audience-targeting",
    icon: "Crosshair",
    accent: "denim",
    category: "intelligence",
  },
  {
    code: "C09",
    title: "Content Optimization",
    description: "Continuous content refinement — A/B testing, performance analytics, and machine-speed iteration.",
    href: "/capabilities/content-optimization",
    icon: "Sliders",
    accent: "emerald",
    category: "optimization",
  },
  {
    code: "C10",
    title: "Cultural Localization",
    description: "Cultural, linguistic, and regional adaptation — narratives calibrated to local nuance across global markets.",
    href: "/capabilities/cultural-localization",
    icon: "Globe2",
    accent: "cyan",
    category: "optimization",
  },
  {
    code: "C11",
    title: "Automated Influence",
    description: "Autonomous influence systems — from persona networks to algorithmic amplification, operating at machine speed.",
    href: "/capabilities/automated-influence",
    icon: "Bot",
    accent: "purple",
    category: "deployment",
  },
  {
    code: "C12",
    title: "Cognitive Bias Engineering",
    description: "Cognitive bias mapping and exploitation — calibrated to audience psychology, never to disinformation.",
    href: "/capabilities/cognitive-bias",
    icon: "Brain",
    accent: "crimson",
    category: "intelligence",
  },
  {
    code: "C13",
    title: "Meme Culture",
    description: "Meme culture fluency — engineered participation in cultural conversation with authentic voice.",
    href: "/capabilities/meme-culture",
    icon: "Smile",
    accent: "amber",
    category: "creation",
  },
  {
    code: "C14",
    title: "Message Architecture",
    description: "Systematic message design — core, supporting, and contextual layers engineered for maximum penetration.",
    href: "/message-architecture",
    icon: "MessageSquare",
    accent: "denim",
    category: "creation",
  },
  {
    code: "C15",
    title: "Story Framework",
    description: "Story frameworks and narrative structures — adapted from cinema, journalism, and brand strategy.",
    href: "/story-framework",
    icon: "BookOpen",
    accent: "violet",
    category: "creation",
  },
  {
    code: "C16",
    title: "Trend Seeding",
    description: "Trend identification and seeding — early detection of cultural inflection points and engineered participation.",
    href: "/capabilities/trend-seeding",
    icon: "Flame",
    accent: "rose",
    category: "intelligence",
  },
  {
    code: "C17",
    title: "Viral Narrative Injection",
    description: "Viral mechanics engineering — calibrated to platform algorithms and audience psychology.",
    href: "/capabilities/viral-narrative-injection",
    icon: "Rocket",
    accent: "crimson",
    category: "deployment",
  },
  {
    code: "C18",
    title: "Asset Creation & Management",
    description: "End-to-end asset creation, version control, and lifecycle management across the perception stack.",
    href: "/asset-creation-management",
    icon: "Layers",
    accent: "emerald",
    category: "creation",
  },
  {
    code: "C19",
    title: "Sentiment Analysis",
    description: "144-dimensional sentiment analysis — beyond keyword-level polarity to true emotional and intent modeling.",
    href: "/sentiment-analysis",
    icon: "Activity",
    accent: "purple",
    category: "intelligence",
  },
  {
    code: "C20",
    title: "Reputation Management",
    description: "Continuous reputation engineering — monitoring, defense, and rehabilitation across stakeholders and surfaces.",
    href: "/reputation-management",
    icon: "ShieldCheck",
    accent: "cyan",
    category: "response",
  },
];

export interface CapabilityStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const capabilitiesStats: CapabilityStat[] = [
  { value: "20", label: "Proprietary Capabilities", to: 20, icon: "Layers", accent: "crimson" },
  { value: "144", label: "Emotional Dimensions", to: 144, icon: "Activity", accent: "purple" },
  { value: "95–98%", label: "Organic Authenticity", to: 98, suffix: "%", icon: "BadgeCheck", accent: "emerald" },
  { value: "50+", label: "Platform Surfaces", to: 50, suffix: "+", icon: "Globe", accent: "cyan" },
  { value: "5", label: "Capability Categories", to: 5, icon: "Grid3x3", accent: "amber" },
];

export interface CapabilityFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const capabilitiesFeatures: CapabilityFeature[] = [
  {
    title: "Composable Architecture",
    description: "Every capability is a composable primitive. Combine them into bespoke operational sequences.",
    icon: "Workflow",
    accent: "crimson",
  },
  {
    title: "Real-Time Orchestration",
    description: "Capabilities execute at machine speed. Co-occurrence patterns detected and acted upon in milliseconds.",
    icon: "Zap",
    accent: "purple",
  },
  {
    title: "Continuous Learning",
    description: "Every capability improves with every engagement. The platform refines its models continuously.",
    icon: "Repeat",
    accent: "cyan",
  },
  {
    title: "Cross-Capability Coherence",
    description: "Capabilities operate as a unified mesh, not a disjointed toolkit. Outputs align across all surfaces.",
    icon: "Network",
    accent: "emerald",
  },
  {
    title: "Defense-Grade Security",
    description: "All capabilities operate within the Perception X2 zero-trust security perimeter.",
    icon: "ShieldCheck",
    accent: "amber",
  },
  {
    title: "API-Native Access",
    description: "Every capability is accessible via API. Wire Perception X2 capabilities into your existing systems.",
    icon: "Code2",
    accent: "denim",
  },
  {
    title: "Cultural Calibration",
    description: "Each capability adapts to local cultural, regulatory, and linguistic nuance — automatically.",
    icon: "Globe2",
    accent: "violet",
  },
  {
    title: "Audit & Observability",
    description: "Every capability emits detailed telemetry. Full observability across all operational surfaces.",
    icon: "Eye",
    accent: "rose",
  },
];

export interface CapabilityUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const capabilitiesUseCases: CapabilityUseCase[] = [
  {
    title: "Market Launch",
    body: "Engineer category-defining perception from day zero. Combine narrative, content, deployment, and amplification.",
    icon: "Rocket",
    accent: "crimson",
  },
  {
    title: "Crisis Response",
    body: "Activate crisis narrative response, sentiment analysis, and reputation management in coordinated fashion.",
    icon: "Siren",
    accent: "amber",
  },
  {
    title: "Brand Expansion",
    body: "Localize narratives across markets, deploy across cultures, and amplify through coordinated influencer networks.",
    icon: "Globe",
    accent: "purple",
  },
  {
    title: "Viral Campaign",
    body: "Engineer viral mechanics through story framework, trend seeding, and narrative injection — calibrated to platform algorithms.",
    icon: "Flame",
    accent: "rose",
  },
  {
    title: "Public Trust",
    body: "Build long-term public trust through emotional resonance, audience targeting, and message architecture.",
    icon: "Heart",
    accent: "emerald",
  },
  {
    title: "Competitive Edge",
    body: "Pre-empt competitor narratives through trend seeding, sentiment analysis, and content optimization.",
    icon: "Swords",
    accent: "cyan",
  },
];

export interface CapabilityWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const capabilitiesWhy: CapabilityWhy[] = [
  {
    title: "Capability Density",
    body: "20 capabilities, each refined through 15+ years of operational excellence — unmatched in the perception management category.",
    metric: "20",
    accent: "crimson",
  },
  {
    title: "Composability",
    body: "Every capability is API-accessible and composable. Build bespoke operational sequences for any engagement pattern.",
    metric: "100%",
    accent: "purple",
  },
  {
    title: "Cross-Capability Learning",
    body: "Insights from one capability flow into every other. The platform improves as a whole, not in isolation.",
    metric: "Continuous",
    accent: "cyan",
  },
  {
    title: "Operational Velocity",
    body: "Capabilities execute at machine speed — sub-second for intelligence operations, sub-hour for response operations.",
    metric: "<60m",
    accent: "emerald",
  },
  {
    title: "Authenticity Floor",
    body: "Every capability maintains the 95–98% organic authenticity floor. No detection, no suspicion, ever.",
    metric: "98%",
    accent: "amber",
  },
  {
    title: "Production-Grade",
    body: "Capabilities operate in the world's most demanding environments. Zero security incidents in 15+ years.",
    metric: "0",
    accent: "denim",
  },
];

export interface CapabilityFaq {
  q: string;
  a: string;
}

export const capabilitiesFaq: CapabilityFaq[] = [
  {
    q: "What are Perception X2 capabilities?",
    a: "Perception X2 capabilities are 20 composable operational primitives — each a coherent unit of perception engineering that can be deployed independently or composed into bespoke operational sequences. Together, they form the world's most advanced perception amplification stack.",
  },
  {
    q: "How are capabilities categorized?",
    a: "Capabilities are organized into five categories: Creation (narrative, content, story), Deployment (multi-platform, organic, influencer, automated, viral), Intelligence (emotional resonance, audience targeting, cognitive bias, trend seeding, sentiment), Response (crisis narrative, reputation), and Optimization (content optimization, cultural localization).",
  },
  {
    q: "Can I use individual capabilities?",
    a: "Yes. Each capability is API-accessible and can be deployed independently. However, capabilities compose most effectively as a unified mesh — engagement outcomes are typically 3–5× higher when capabilities are orchestrated together.",
  },
  {
    q: "How do capabilities integrate with the platform?",
    a: "Every capability runs on the Perception X2 platform's data fabric, engine orchestration layer, and security perimeter. Capabilities are versioned, monitored, and continuously improved through the platform's learning systems.",
  },
  {
    q: "What makes these capabilities different?",
    a: "Each capability is refined through 15+ years of operational excellence across 300+ elite engagements. They are not theoretical — they are production-grade, proven in the world's most demanding environments.",
  },
  {
    q: "How fast can capabilities be deployed?",
    a: "Standard capability deployments reach operational status in 4–6 weeks. Crisis-class deployments activate in under 60 minutes. API-level capability access can be granted within hours for existing clients.",
  },
  {
    q: "What is the 144-dimensional emotional resonance capability?",
    a: "The Emotional Resonance capability maps content and audience response across 144 distinct emotional dimensions — far beyond the 5–10 dimensions used by conventional platforms. This is the analytical foundation of every other capability in the stack.",
  },
  {
    q: "Do capabilities work across all platforms?",
    a: "Yes. Every capability is calibrated to operate across the 50+ platforms Perception X2 covers — including social, search, news, forums, reviews, video, podcasts, and emerging surfaces.",
  },
];

export const capabilitiesCta = {
  eyebrow: "Compose Your Stack",
  title: "20 capabilities. One autonomous platform. Infinite operational patterns.",
  description:
    "The Perception X2 capability stack is the most advanced perception engineering surface ever built. Compose the capabilities that match your engagement — and let the platform orchestrate the rest.",
  primary: { label: "Request a Capability Briefing", href: "/contact" },
  secondary: { label: "Explore the Platform", href: "/platform" },
  trustItems: [
    "20 capabilities",
    "5 categories",
    "144-D emotional resonance",
    "50+ platforms",
    "API-native access",
  ],
};

/* Capability category distribution data for chart */
export const capabilityCategoryBreakdown = [
  { name: "Creation", count: 6, accent: "var(--color-crimson)" },
  { name: "Deployment", count: 5, accent: "var(--color-purple)" },
  { name: "Intelligence", count: 5, accent: "var(--color-cyan)" },
  { name: "Response", count: 2, accent: "var(--color-amber)" },
  { name: "Optimization", count: 2, accent: "var(--color-emerald)" },
];
